import React, { useState } from 'react';

const ExportOptionsModal = ({
  isOpen,
  onClose,
  onExport,
  campaignName,
  // Current filter state from CampaignContentView (optional)
  currentFilters = {}
}) => {
  const [exportScope, setExportScope] = useState('all'); // 'all', 'filtered', 'custom'
  const [contentType, setContentType] = useState('all'); // 'all', 'streams', 'videos'
  const [platform, setPlatform] = useState('all'); // 'all', 'twitch', 'youtube', 'tiktok'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  if (!isOpen) return null;

  const handleExport = (exportContentType = null) => {
    const options = {
      contentType: exportContentType || contentType,
      platform: exportScope === 'filtered' ? (currentFilters.platform || 'all') : platform,
      startDate: exportScope === 'filtered' ? (currentFilters.startDate || null) : (startDate || null),
      endDate: exportScope === 'filtered' ? (currentFilters.endDate || null) : (endDate || null)
    };

    // If exporting all, clear filters
    if (exportScope === 'all' && !exportContentType) {
      options.contentType = 'all';
      options.platform = 'all';
      options.startDate = null;
      options.endDate = null;
    }

    onExport(options);
    onClose();
  };

  const hasActiveFilters = currentFilters.platform !== 'all' ||
    currentFilters.contentType !== 'all' ||
    currentFilters.startDate ||
    currentFilters.endDate;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-2">Export Campaign Report</h2>
        <p className="text-gray-600 mb-4">
          Export data for <strong>{campaignName}</strong>
        </p>

        {/* Quick Export Buttons */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 mb-3">Quick Export</p>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => handleExport('all')}
              className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex flex-col items-center gap-1"
            >
              <span className="text-lg">📦</span>
              <span className="text-sm font-medium">All Content</span>
            </button>
            <button
              onClick={() => handleExport('streams')}
              className="px-4 py-3 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 flex flex-col items-center gap-1"
            >
              <span className="text-lg">🎮</span>
              <span className="text-sm font-medium">Streams Only</span>
            </button>
            <button
              onClick={() => handleExport('videos')}
              className="px-4 py-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex flex-col items-center gap-1"
            >
              <span className="text-lg">🎬</span>
              <span className="text-sm font-medium">Videos Only</span>
            </button>
          </div>
        </div>

        <div className="border-t pt-4">
          <p className="text-sm font-medium text-gray-700 mb-3">Or customize your export</p>

          {/* Export Scope */}
          <div className="space-y-2 mb-4">
            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="exportScope"
                value="all"
                checked={exportScope === 'all'}
                onChange={(e) => setExportScope(e.target.value)}
              />
              <div>
                <div className="font-medium text-sm">Export All Data</div>
                <div className="text-xs text-gray-500">All content regardless of filters</div>
              </div>
            </label>

            {hasActiveFilters && (
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 border-purple-200 bg-purple-50">
                <input
                  type="radio"
                  name="exportScope"
                  value="filtered"
                  checked={exportScope === 'filtered'}
                  onChange={(e) => setExportScope(e.target.value)}
                />
                <div>
                  <div className="font-medium text-sm text-purple-900">Export Current View</div>
                  <div className="text-xs text-purple-700">
                    {currentFilters.platform !== 'all' && `${currentFilters.platform} • `}
                    {currentFilters.contentType !== 'all' && `${currentFilters.contentType} • `}
                    {currentFilters.startDate && `From ${currentFilters.startDate} `}
                    {currentFilters.endDate && `To ${currentFilters.endDate}`}
                    {!currentFilters.startDate && !currentFilters.endDate && currentFilters.platform === 'all' && currentFilters.contentType !== 'all' && ''}
                  </div>
                </div>
              </label>
            )}

            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="exportScope"
                value="custom"
                checked={exportScope === 'custom'}
                onChange={(e) => setExportScope(e.target.value)}
              />
              <div>
                <div className="font-medium text-sm">Custom Filters</div>
                <div className="text-xs text-gray-500">Choose specific filters below</div>
              </div>
            </label>
          </div>

          {/* Custom Filters */}
          {exportScope === 'custom' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg mb-4">
              {/* Content Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
                <div className="flex gap-2">
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'streams', label: 'Streams' },
                    { value: 'videos', label: 'Videos' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setContentType(opt.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        contentType === opt.value
                          ? 'bg-purple-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Platform */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'twitch', label: 'Twitch' },
                    { value: 'kick', label: 'Kick' },
                    { value: 'youtube', label: 'YouTube' },
                    { value: 'tiktok', label: 'TikTok' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setPlatform(opt.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        platform === opt.value
                          ? 'bg-purple-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Range (optional)</label>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Start date"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="End date"
                  />
                </div>
              </div>
            </div>
          )}

          {/* YouTube API attribution */}
          <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg mb-4">
            <svg viewBox="0 3.5 24 17" fill="#FF0000" className="h-[21px] w-auto flex-shrink-0 mt-0.5" role="img" aria-label="YouTube">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            <p className="text-xs text-gray-500">
              YouTube data provided by the YouTube API Services. Data is valid for 30 days from the fetch date per YouTube API Terms of Service.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleExport()}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Export CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportOptionsModal;
